import type { UseFormReturn } from 'react-hook-form';
import {
  Labeled,
  OptionCard,
  Radio,
  RadioGroup,
  Select,
  TextInputField,
} from '@sixui/ui';
import { spacingTokens } from '@sixui/ui/themes/base/spacing.stylex';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IFormMode } from '~/helpers/types';
import type { IAdFormValues } from './AdForm';
import { FormDateField } from '~/components/atoms/fields/DateField';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { FormFileUploader } from '~/components/organisms/fields/FileUploader';

export type IAdFormMainSectionProps = IContainerProps & {
  form: UseFormReturn<IAdFormValues>;
  mode: IFormMode;
  appId: string;
};

const styles = stylex.create({
  typeOptions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridAutoRows: '1fr',
    gap: spacingTokens.padding$2,
  },
});

export const AdFormMainSection: React.FC<IAdFormMainSectionProps> = (props) => {
  const { sx, form, appId } = props;
  const { t } = useTranslation();

  return (
    <FormSection sx={sx}>
      <Labeled
        label={t('AdFormMainSection.fields.name.label')}
        supportingText={t('AdFormMainSection.fields.name.supportingText')}
        required
      >
        <FormField
          as={TextInputField}
          forwardError
          control={form.control}
          name="name"
        />
      </Labeled>

      <Labeled
        label={t('AdFormMainSection.fields.url.label')}
        supportingText={t('AdFormMainSection.fields.url.supportingText')}
        required
      >
        <FormField
          as={TextInputField}
          forwardError
          control={form.control}
          name="url"
          type="url"
        />
      </Labeled>

      <Labeled
        label={t('AdFormMainSection.fields.mediaId.label')}
        supportingText={t('AdFormMainSection.fields.mediaId.supportingText')}
        required
      >
        <FormFileUploader
          name="medias"
          control={form.control}
          appId={appId}
          maxFiles={1}
          acceptTypes={['picture', 'video']}
        />
      </Labeled>

      <Labeled
        label={t('AdFormMainSection.fields.location.label')}
        supportingText={t('AdFormMainSection.fields.location.supportingText')}
        required
      >
        <FormField
          name="location"
          control={form.control}
          as={RadioGroup}
          rules={{ required: true }}
          sx={styles.typeOptions}
        >
          <OptionCard
            as={Radio}
            label={t('AdFormMainSection.fields.location.options.feed.label')}
            value="feed"
          >
            {t('AdFormMainSection.fields.location.options.feed.supportingText')}
          </OptionCard>
          <OptionCard
            as={Radio}
            label={t('AdFormMainSection.fields.location.options.article.label')}
            value="article"
          >
            {t(
              'AdFormMainSection.fields.location.options.article.supportingText',
            )}
          </OptionCard>
        </FormField>
      </Labeled>

      {form.watch('location') === 'feed' && (
        <>
          <Labeled
            label={t('AdFormMainSection.fields.feedFormat.label')}
            required
          >
            <FormField
              name="feedFormat"
              control={form.control}
              as={Select}
              forwardError
              items={[
                {
                  value: 'article',
                  label: t(
                    'AdFormMainSection.fields.feedFormat.options.article.label',
                  ),
                  supportingText: t(
                    'AdFormMainSection.fields.feedFormat.options.article.supportingText',
                  ),
                },
                {
                  value: 'banner',
                  label: t(
                    'AdFormMainSection.fields.feedFormat.options.banner.label',
                  ),
                  supportingText: t(
                    'AdFormMainSection.fields.feedFormat.options.banner.supportingText',
                  ),
                },
              ]}
            />
          </Labeled>

          <Labeled
            label={t('AdFormMainSection.fields.feedPosition.label')}
            required
          >
            <FormField
              name="feedPosition"
              control={form.control}
              as={Select}
              forwardError
              items={[
                {
                  value: 'fixed',
                  label: t(
                    'AdFormMainSection.fields.feedPosition.options.fixed.label',
                  ),
                },
                {
                  value: 'recurrent',
                  label: t(
                    'AdFormMainSection.fields.feedPosition.options.recurrent.label',
                  ),
                },
              ]}
            />
          </Labeled>

          {form.watch('feedPosition') === 'fixed' && (
            <Labeled
              label={t('AdFormMainSection.fields.feedFixedIndex.label')}
              supportingText={t(
                'AdFormMainSection.fields.feedFixedIndex.supportingText',
              )}
              required
            >
              <FormField
                as={TextInputField}
                forwardError
                control={form.control}
                name="feedIndex"
                type="number"
                min={0}
              />
            </Labeled>
          )}

          {form.watch('feedPosition') === 'recurrent' && (
            <>
              <Labeled
                label={t(
                  'AdFormMainSection.fields.feedRecurrentMinIndex.label',
                )}
                supportingText={t(
                  'AdFormMainSection.fields.feedRecurrentMinIndex.supportingText',
                )}
                required
              >
                <FormField
                  as={TextInputField}
                  forwardError
                  control={form.control}
                  name="feedIndex"
                  type="number"
                  min={0}
                />
              </Labeled>

              <Labeled
                label={t('AdFormMainSection.fields.feedRecurrentStep.label')}
                supportingText={t(
                  'AdFormMainSection.fields.feedRecurrentStep.supportingText',
                )}
                required
              >
                <FormField
                  as={TextInputField}
                  forwardError
                  control={form.control}
                  name="feedRecurrentStep"
                  type="number"
                  min={0}
                />
              </Labeled>

              <Labeled
                label={t(
                  'AdFormMainSection.fields.feedRecurrentMaxIndex.label',
                )}
                supportingText={t(
                  'AdFormMainSection.fields.feedRecurrentMaxIndex.supportingText',
                )}
              >
                <FormField
                  as={TextInputField}
                  forwardError
                  control={form.control}
                  name="feedReccurentMaxIndex"
                  type="number"
                  min={form.watch('feedIndex') || 0}
                />
              </Labeled>
            </>
          )}
        </>
      )}

      {form.watch('location') === 'article' && (
        <Labeled
          label={t('AdFormMainSection.fields.articlePosition.label')}
          supportingText={t(
            'AdFormMainSection.fields.articlePosition.supportingText',
          )}
          required
        >
          <FormField
            name="articlePosition"
            control={form.control}
            as={Select}
            forwardError
            items={[
              {
                value: 'before',
                label: t(
                  'AdFormMainSection.fields.articlePosition.options.before.label',
                ),
              },
              {
                value: 'after',
                label: t(
                  'AdFormMainSection.fields.articlePosition.options.after.label',
                ),
              },
            ]}
          />
        </Labeled>
      )}

      <Labeled label={t('AdFormMainSection.fields.startAt.label')} required>
        <FormDateField
          name="startAt"
          control={form.control}
          defaultValue={new Date().toISOString()}
        />
      </Labeled>

      <Labeled label={t('AdFormMainSection.fields.endAt.label')} required>
        <FormDateField name="endAt" control={form.control} />
      </Labeled>

      <Labeled
        label={t('AdFormMainSection.fields.maxDisplays.label')}
        supportingText={t(
          'AdFormMainSection.fields.maxDisplays.supportingText',
        )}
      >
        <FormField
          as={TextInputField}
          forwardError
          control={form.control}
          name="maxDisplays"
          type="number"
          min={0}
        />
      </Labeled>

      <Labeled
        label={t('AdFormMainSection.fields.maxClicks.label')}
        supportingText={t('AdFormMainSection.fields.maxClicks.supportingText')}
      >
        <FormField
          as={TextInputField}
          forwardError
          control={form.control}
          name="maxClicks"
          type="number"
          min={0}
        />
      </Labeled>
    </FormSection>
  );
};
